<template>
  <div>
    <div class="mb-8 flex justify-start max-w-3xl">
      <h1 class="font-bold text-3xl">
        <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('campaigns')">Campanhas</inertia-link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ form.name }}
      </h1>
      <img v-if="campaign.photo" class="block w-8 h-8 rounded-full ml-4" :src="campaign.photo" alt="Photo" />
    </div>

    <trashed-message v-if="campaign.deleted_at" class="mb-6" @restore="restore">
      <!--This campaigns has been deleted.-->
      Esta campanha foi excluída.
    </trashed-message>
    <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          <select-input v-if="showOrganizations" v-model="form.organization_id" :error="form.errors.organization_id" class="pr-6 pb-8 w-full" label="Organização">
            <option :value="null" />
            <option v-for="organization in organizations" :key="organization.id" :value="organization.id">{{ organization.name }}</option>
          </select-input>
          <text-input v-model="form.name" :error="form.errors.name" class="pr-6 pb-8 w-full " label="Nome" />
          <text-input v-model="form.label" :error="form.errors.label" class="pr-6 pb-8 w-full " label="Título da Ação" />
          <!-- <textarea-input v-model="form.description" :value="form.description" :error="form.errors.description" class="pr-6 pb-8 w-full " label="Descrição" /> -->
          <label class="pr-6 w-full" for="description" :error="form.errors.description">
            Descrição: <small v-if="form.errors.description" class="form-error">{{ form.errors.description }}</small>
          </label>
          <v-md-editor
            id="description"
            v-model="form.description"
            left-toolbar="undo redo clear | h bold italic strikethrough quote | ul ol table hr | link code"
            right-toolbar="preview sync-scroll"
            class="form-input mt-3 mr-6 mb-8 w-full"
            :class="{ 'error': form.errors.description }"
            height="400px"
          />
          URL:&nbsp;[<a :href="campaign.url" class="no-underline hover:underline" target="_blank">abrir</a>]<br /><br />
          <text-input v-model="form.url" :error="form.errors.url" class="pr-6 pb-8 w-full " placeholder="Ex.: https://www.google.com" />
          <text-input v-model="form.coordinates" :error="form.errors.coordinates" class="pr-6 pb-8 w-full " label="Coordenadas" :class="{ 'hidden': !showOrganizations }" />
          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <text-input v-model="form.major" :error="form.errors.major" class="pr-6 pb-8 w-full " label="Major (Beacon)" :class="{ 'hidden': !showOrganizations }" />
          </div>
          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <text-input v-model="form.minor" :error="form.errors.minor" class="pr-6 pb-8 w-full " label="Minor (Beacon)" :class="{ 'hidden': !showOrganizations }" />
          </div>
          <div class="pr-6 pb-8 w-full ">
            <label>Data Inicial:</label><br /><br />
            <datepicker v-model="form.start_date" :language="ptBR" :error="form.errors.start_date" :format="customFormatter" placeholder="Selecione..." />
          </div>
          <div class="pr-6 pb-8 w-full ">
            <label>Data Final:</label><br /><br />
            <datepicker v-model="form.end_date" :language="ptBR" :error="form.errors.end_date" :format="customFormatter" placeholder="Selecione..." />
          </div>
          <div class="pr-6 pb-8 w-full ">
            <label class="mt-6 select-none flex items-center" for="sunday">
              <input id="sunday" v-model="form.sunday" class="mr-1" type="checkbox" />
              <span class="text-sm">Domingo</span>
            </label>
            <label class="mt-6 select-none flex items-center" for="monday">
              <input id="monday" v-model="form.monday" class="mr-1" type="checkbox" />
              <span class="text-sm">Segunda-feira</span>
            </label>
            <label class="mt-6 select-none flex items-center" for="tuesday">
              <input id="tuesday" v-model="form.tuesday" class="mr-1" type="checkbox" />
              <span class="text-sm">Terça-feira</span>
            </label>
            <label class="mt-6 select-none flex items-center" for="wednesday">
              <input id="wednesday" v-model="form.wednesday" class="mr-1" type="checkbox" />
              <span class="text-sm">Quarta-feira</span>
            </label>
            <label class="mt-6 select-none flex items-center" for="thursday">
              <input id="thursday" v-model="form.thursday" class="mr-1" type="checkbox" />
              <span class="text-sm">Quinta-feira</span>
            </label>
            <label class="mt-6 select-none flex items-center" for="friday">
              <input id="friday" v-model="form.friday" class="mr-1" type="checkbox" />
              <span class="text-sm">Sexta</span>
            </label>
            <label class="mt-6 select-none flex items-center" for="saturday">
              <input id="saturday" v-model="form.saturday" class="mr-1" type="checkbox" />
              <span class="text-sm">Sábado</span>
            </label>
          </div>
          <div class="flex w-full">
            <file-input v-model="form.photo" :error="form.errors.photo" class="pr-6 pb-8 w-full" type="file" accept="image/*" label="Imagem" />
            <a v-if="campaign.photo" class="my-auto mr-3 btn-indigo" :href="campaign.photo" target="_blank">
              Visualizar
            </a>
            <button
              v-if="campaign.photo"
              class="hover:underline w-1/8"
              type="button"
              @click="remove('photo')"
            >
              <icon name="trash" class="flex-shrink-0 mb-3 w-4 h-4 fill-red-600" />
            </button>
          </div>
          <div class="flex w-full">
            <file-input v-model="form.audio" :error="form.errors.audio" class="pr-6 pb-8 w-full " type="file" accept="audio/*" label="Áudio" />
            <a v-if="campaign.audio" class="my-auto mr-3 btn-indigo" :href="campaign.audio" target="_blank">
              Ouvir
            </a>
            <button
              v-if="campaign.audio"
              class="hover:underline w-1/8"
              type="button"
              @click="remove('audio')"
            >
              <icon name="trash" class="flex-shrink-0 mb-3 w-4 h-4 fill-red-600" />
            </button>
          </div>
          <select-input v-model="form.active" :error="form.errors.active" class="pr-6 pb-8 w-full " label="Ativo" :class="{ 'hidden': !showOrganizations }">
            <option :value="null" />
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select-input>
        </div>

        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center">
          <button v-if="!campaign.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Deletar Campanha</button>
          <button class="btn-generate-qrcode ml-auto" type="button">
            <a v-if="campaign.interaction" :href="campaign.interaction" target="_blank">
              Interação
            </a>
          </button>&nbsp;
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Atualizar Campanha</loading-button>
        </div>
      </form>
    </div>
    <h2 class="mt-12 font-bold text-2xl" :class="{ 'hidden': !showOrganizations }">QRCodes</h2>
    <div class="mt-6 bg-white rounded shadow overflow-x-auto" :class="{ 'hidden': !showOrganizations }">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Nome</th>
          <th class="px-6 pt-6 pb-4" colspan="2">URL</th>
        </tr>
        <tr v-for="qrcode in campaign.qrcodes" :key="qrcode.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('qrcodes.edit', qrcode.id)">
              {{ qrcode.name }}
              <icon v-if="qrcode.deleted_at" name="trash" class="flex-shrink-0 w-3 h-3 fill-gray-400 ml-2" />
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('qrcodes.edit', qrcode.id)" tabindex="-1">
              <span class="text-xs px-2 py-0.5 font-bold bg-gray-100 text-gray-600 rounded">
                {{ qrcode.url }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t w-px">
            <inertia-link class="px-4 flex items-center" :href="route('qrcodes.edit', qrcode.id)" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </inertia-link>
          </td>
        </tr>
        <tr v-if="campaign.qrcodes.length === 0">
          <td class="border-t px-6 py-4" colspan="4"><!--No qrcodes found.--> Nenhum registro</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import FileInput from '@/Shared/FileInput'
// import TextareaInput from '@/Shared/TextareaInput'
import Datepicker from 'vuejs-datepicker'
import moment from 'moment'
import {ptBR} from 'vuejs-datepicker/dist/locale'

export default {
  metaInfo() {
    return { title: this.form.name }
  },
  components: {
    Icon,
    // TextareaInput,
    FileInput,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
    Datepicker,
  },
  layout: Layout,
  remember: 'form',
  props: {
    campaign: Object,
    organizations: Array,
    showOrganizations: Boolean,
  },
  data() {
    return {
      ptBR: ptBR,
      form: this.$inertia.form({
        _method: 'put',
        organization_id: this.campaign.organization_id,
        name: this.campaign.name,
        label: this.campaign.label,
        description: this.campaign.description,
        url: this.campaign.url,
        coordinates: this.campaign.coordinates,
        major: this.campaign.major,
        minor: this.campaign.minor,
        start_date: this.campaign.start_date,
        end_date: this.campaign.end_date,
        sunday: this.campaign.sunday,
        monday: this.campaign.monday,
        tuesday: this.campaign.tuesday,
        wednesday: this.campaign.wednesday,
        thursday: this.campaign.thursday,
        friday: this.campaign.friday,
        saturday: this.campaign.saturday,
        photo: null,
        audio: null,
        active: this.campaign.active,
      }),
    }
  },
  methods: {
    customFormatter(date) {
      moment.locale('pt-br')
      return moment(date).format('L')
    },
    update() {
      this.form.post(this.route('campaigns.update', this.campaign.id).url())
    },
    destroy() {
      // Are you sure you want to delete this campaign?
      if (confirm('Tem certeza de que deseja excluir esta campanha?')) {
        this.$inertia.delete(this.route('campaigns.destroy', this.campaign.id))
      }
    },
    remove(data) {
      if (confirm(`Tem certeza de que deseja remover ${data == 'photo' ? 'a Imagem' : 'o Áudio'}?`)) {
        this.$inertia.put(this.route('campaigns.remove', [this.campaign.id, data]))
      }
    },
    restore() {
      // Are you sure you want to restore this campaign?
      if (confirm('Tem certeza de que deseja restaurar esta campanha?')) {
        this.$inertia.put(this.route('campaigns.restore', this.campaign.id))
      }
    },
  },
}
</script>
